import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Radio,
  Select,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import type { LegacyCaseDetail } from "../../../types/legacyCases";
import { useLegacyPatients } from "../../../hooks/useLegacyCases";
import {
  formatLegacyDate,
  type LegacyReviewForm,
} from "../../../utils/legacyReviewForm";
import {
  legacyPatientIdentity,
  mergeLegacyPatientCandidates,
  sameLegacyPatientIdentity,
  type LegacyPatientCandidate,
} from "../../../utils/legacyPatientMapping";
import LegacyPatientIdentityComparison from "./LegacyPatientIdentityComparison";
import LegacyNewPatientFields from "./LegacyNewPatientFields";

interface Props {
  form: LegacyReviewForm;
  detail: LegacyCaseDetail;
  createCandidates: LegacyPatientCandidate[];
  checkingCandidates: boolean;
  candidateError?: Error | null;
  onRefreshCandidates: () => void;
}

export default function LegacyPatientFields({
  form,
  detail,
  createCandidates,
  checkingCandidates,
  candidateError,
  onRefreshCandidates,
}: Props) {
  const [search, setSearch] = useState("");
  const [query] = useDebouncedValue(search, 300);
  const patients = useLegacyPatients(detail.id, query);
  const queryClient = useQueryClient();
  const detailUpdatedAt =
    queryClient.getQueryState(["legacy-case", detail.id])?.dataUpdatedAt ?? 0;
  const [retained, setRetained] = useState(
    detail.patientCandidates.find(
      (patient) => patient.id === form.values.patientId
    )
  );
  const candidates = useMemo(
    () =>
      mergeLegacyPatientCandidates(
        detail.patientCandidates,
        patients.data ?? [],
        retained,
        patients.dataUpdatedAt >= detailUpdatedAt
      ),
    [
      detail.patientCandidates,
      detailUpdatedAt,
      patients.data,
      patients.dataUpdatedAt,
      retained,
    ]
  );
  const selected = useMemo(
    () => candidates.find((patient) => patient.id === form.values.patientId),
    [candidates, form.values.patientId]
  );
  const options = useMemo(
    () =>
      candidates
        .map((patient) => ({
          value: patient.id,
          label: `${patient.lastName}, ${
            patient.firstName
          } · DOB ${formatLegacyDate(patient.dateOfBirth)} · MRN ${
            patient.medicalRecordNumber || "—"
          }`,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [candidates]
  );
  const verified = useMemo(
    () =>
      sameLegacyPatientIdentity(form.values.patientReviewedIdentity, selected),
    [form.values.patientReviewedIdentity, selected]
  );
  const handleSearch = useCallback((value: string) => setSearch(value), []);
  const handleSelect = useCallback(
    (value: string | null) => {
      form.setFieldValue("patientId", value || "");
      form.setFieldValue("patientReviewedIdentity", undefined);
      form.setFieldValue("patientReviewedCandidateIds", undefined);
      const patient = candidates.find((candidate) => candidate.id === value);
      setRetained(patient);
    },
    [candidates, form]
  );
  const handleAction = useCallback(
    (value: string) => {
      if (value === "match" || value === "create") {
        form.setFieldValue("patientAction", value);
        form.setFieldValue("patientReviewedIdentity", undefined);
        form.setFieldValue("patientReviewedCandidateIds", undefined);
      }
    },
    [form]
  );
  const cacheCurrentCandidate = useCallback(
    (patient: LegacyPatientCandidate) => {
      queryClient.setQueryData<LegacyCaseDetail>(
        ["legacy-case", detail.id],
        (current) =>
          current
            ? {
                ...current,
                patientCandidates: [
                  ...current.patientCandidates.filter(
                    (entry) => entry.id !== patient.id
                  ),
                  patient,
                ],
              }
            : current
      );
    },
    [detail.id, queryClient]
  );
  const handleUsePatient = useCallback(() => {
    if (!selected) return;
    form.setFieldValue(
      "patientReviewedIdentity",
      legacyPatientIdentity(selected)
    );
    form.clearFieldError("patientId");
    cacheCurrentCandidate(selected);
  }, [cacheCurrentCandidate, form, selected]);
  const handleCandidateChoice = useCallback(
    (patientId: string) => {
      const candidate = createCandidates.find(
        (patient) => patient.id === patientId
      );
      if (!candidate) return;
      setRetained(candidate);
      cacheCurrentCandidate(candidate);
      form.setValues({
        patientAction: "match",
        patientId,
        patientReviewedIdentity: undefined,
        patientReviewedCandidateIds: undefined,
      });
    },
    [cacheCurrentCandidate, createCandidates, form]
  );

  return (
    <Stack gap="md">
      <Title order={3}>Patient</Title>
      <Radio.Group
        label="Patient record"
        withAsterisk
        aria-required
        {...form.getInputProps("patientAction")}
        onChange={handleAction}
      >
        <Stack gap="xs" mt="xs">
          <Radio value="match" label="Match an existing patient" />
          <Radio value="create" label="Create a patient on import" />
        </Stack>
      </Radio.Group>
      {form.values.patientAction === "match" && (
        <Stack gap="sm">
          <Select
            label="Existing patient"
            withAsterisk
            aria-required
            searchable
            clearable
            data={options}
            nothingFoundMessage={
              query.length < 2
                ? "Type at least 2 characters to search"
                : "No matching patients"
            }
            searchValue={search}
            onSearchChange={handleSearch}
            {...form.getInputProps("patientId")}
            onChange={handleSelect}
            comboboxProps={{
              withinPortal: true,
              position: "bottom-start",
              offset: 8,
            }}
          />
          {patients.isFetching && (
            <Text size="sm" role="status">
              Searching patients…
            </Text>
          )}
          {patients.error && (
            <Alert color="red" title="Patient search unavailable">
              {patients.error.message}
            </Alert>
          )}
          {selected && (
            <Stack gap="sm">
              <LegacyPatientIdentityComparison
                detail={detail}
                values={form.values}
                patient={selected}
              />
              {verified ? (
                <Alert
                  color="green"
                  title="Patient identity reviewed"
                  role="status"
                >
                  Import will link to this existing patient. Current LIS
                  demographics and the historical report will stay unchanged.
                </Alert>
              ) : (
                <Alert
                  color="orange"
                  title="Confirm the patient link"
                  role="status"
                >
                  Compare the evidence above, then choose Use this patient. You
                  can save an unfinished draft before confirming this link.
                </Alert>
              )}
              <Button
                variant="light"
                onClick={handleUsePatient}
                disabled={verified}
              >
                Use this patient
              </Button>
            </Stack>
          )}
        </Stack>
      )}
      {form.values.patientAction !== "match" && (
        <LegacyNewPatientFields
          form={form}
          candidates={createCandidates}
          loading={checkingCandidates}
          error={candidateError}
          onRefresh={onRefreshCandidates}
          onSelect={handleCandidateChoice}
        />
      )}
    </Stack>
  );
}
